import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, Building2, Wallet, Sigma, Boxes, Table2, Users, ClipboardCheck, Database, Tags,
  ChevronLeft, ChevronDown, Check, Loader2, Save, ExternalLink, Eye, Plus, Info, Settings,
  Layers, Building, Grid3x3, Boxes as BoxesIcon, Landmark, Ruler, HardHat, Printer, FileDown, RotateCcw, Maximize2, Cpu, Calculator,
  AlertTriangle, History, Trash2,
} from "lucide-react";
import { api, humanizeAxiosError } from "../../lib/api";
import { runRecalc } from "../../lib/recalcJob";
// P0 · Coordina el foco de edición con el auto-guardado: mientras haya una celda enfocada, el
// setData(fresh) del servidor se aplaza para no remontar el input y robar el foco (Mandato BD 2026-06).
const editState = { active: 0, pendingFresh: null, applyFresh: null, seq: 0, pendingSeq: 0 };
import { useAuth } from "../../context/AuthContext";
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import { generarMemoriaCalculo } from "./memoriaCalculoPDF";

/* Paleta oficial COREX/VM */
const PETROL = "#0c2b38";
const PETROL_2 = "#0f3546";
const PETROL_3 = "#0a2430";
const NEON = "#22c55e";
const BLUE = "#2563eb";
const TEAL = "#0ea5b7";

const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FIERROS = ["1/4\"", "3/8\"", "1/2\"", "5/8\"", "3/4\"", "1\""];

// Proyecto Sandoval en el motor COREX (V2 · S/ 448,287.47) para el Núcleo de Ingeniería.
const COREX_V2_PROJECT_ID = "d57394ea-1667-418a-977f-6a05914019cd";
const COREX_V2_PROJECT_NAME = "01 SANTISTEBAN · VIVIENDA MULTIFAMILIAR (V2)";
const NAV_GROUPS = [
  { label: "Planificación", items: [
    { key: "inicio", label: "INICIO", icon: Home, route: "/software/vivienda-multifamiliar" },
  ]},
  { label: "Núcleo de Ingeniería", items: [
    { key: "corex-nucleo", label: "INGENIERÍA Y GESTIÓN DE COSTOS", icon: Cpu, route: "/software/corex", adminOnly: true, badge: "V2" },
    { key: "mi-presupuesto", label: "MI PRESUPUESTO Y METRADOS", icon: Calculator, clientOnly: true, badge: "PRIVADO" },
  ]},
  { label: "Bases de Ingeniería", items: [
    { key: "base-estructural", label: "BD ESTRUCTURAS", icon: Database, badge: "NUEVO" },
    { key: "actualizacion-precios", label: "PRECIOS $", icon: Tags, badge: "NUEVO", route: "/software/actualizacion-precios?from=estructural" },
  ]},
];

const FAMILIAS = [
  { key: "cimentacion", label: "CIMENTACIÓN", icon: Landmark },
  { key: "vertical", label: "ESTRUCTURA VERTICAL", icon: Building },
  { key: "horizontal", label: "ESTRUCTURA HORIZONTAL", icon: Layers },
  { key: "otros", label: "OTROS ELEMENTOS", icon: Grid3x3 },
];

// Codificación cromática por familia (rediseño 'Vida y Contraste' · solo visual)
const FAM_COLOR = {
  cimentacion: { c: "#1D4ED8", soft: "#EFF5FF", bd: "#BFD6FF" },   // Azul Real
  vertical:    { c: "#EA580C", soft: "#FFF4EC", bd: "#FED7AA" },   // Naranja
  horizontal:  { c: "#059669", soft: "#ECFDF5", bd: "#A7F3D0" },   // Verde Esmeralda
  otros:       { c: "#0891B2", soft: "#ECFEFF", bd: "#A5F3FC" },   // Celeste
};
const SUB_TO_FAM = { zapatas: "cimentacion", viga_cimentacion: "cimentacion", cimiento_corrido: "cimentacion", sobrecimiento: "cimentacion", columnas: "vertical", placas: "vertical", vigas: "horizontal", techo: "horizontal", escalera: "otros", cisterna: "otros" };
const famColorOf = (familyKey) => FAM_COLOR[SUB_TO_FAM[familyKey] || familyKey] || FAM_COLOR.otros;
const FAM_ICON = { cimentacion: Landmark, vertical: Building, horizontal: Layers, otros: Grid3x3 };
const famIconOf = (familyKey) => FAM_ICON[SUB_TO_FAM[familyKey] || familyKey] || Grid3x3;
const ELEMENTO_TIPO = { zapatas: "Zapata", viga_cimentacion: "Viga de cimentación", cimiento_corrido: "Cimiento corrido", sobrecimiento: "Sobrecimiento", columnas: "Columna", placas: "Placa", vigas: "Viga", techo: "Losa aligerada", escalera: "Escalera", cisterna: "Cisterna" };
const VISTA_LABEL = { "3d": "Vista 3D", planta: "Planta", corteX: "Corte X-X", corteY: "Corte Y-Y" };
const VISTAS = [["3d", "3D"], ["planta", "Planta"], ["corteX", "Corte X-X"], ["corteY", "Corte Y-Y"]];

const CIM_SUBS = [
  { key: "zapatas", label: "Zapatas" },
  { key: "viga_cimentacion", label: "Viga de Cimentación" },
  { key: "cimiento_corrido", label: "Cimiento Corrido" },
  { key: "sobrecimiento", label: "Sobrecimiento" },
];

const FAM_SUBS = {
  cimentacion: CIM_SUBS,
  vertical: [{ key: "columnas", label: "Columnas" }, { key: "placas", label: "Placas" }],
  horizontal: [{ key: "vigas", label: "Vigas" }, { key: "techo", label: "Techo / Losa Aligerada" }],
  otros: [{ key: "escalera", label: "Escalera" }, { key: "cisterna", label: "Cisterna" }],
};

// Tipo de tabla por familia (grid = malla X/Y · long = longitudinal+estribos · techo · plain = solo concreto)
const KIND = {
  zapatas: "grid", viga_cimentacion: "long", cimiento_corrido: "plain", sobrecimiento: "grid",
  columnas: "long", placas: "grid", vigas: "long", techo: "techo", escalera: "grid", cisterna: "grid",
};

// Orden oficial del Excel oráculo para la Vista Maestra de Edición Total
const MASTER_ORDER = [
  { key: "zapatas", label: "Zapatas" },
  { key: "viga_cimentacion", label: "Viga de Cimentación" },
  { key: "cimiento_corrido", label: "Cimiento Corrido" },
  { key: "sobrecimiento", label: "Sobrecimiento" },
  { key: "columnas", label: "Columnas" },
  { key: "placas", label: "Placas" },
  { key: "vigas", label: "Vigas" },
  { key: "techo", label: "Techo / Losa Aligerada" },
  { key: "escalera", label: "Escalera" },
  { key: "cisterna", label: "Cisterna" },
];

// Esquema técnico oficial de armado (estilo Excel oráculo "ACERO EN ZAPATA")
const ACERO_ZAPATA_IMG = "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/218205fb8703d7af279216ef7bc39de3603c5ce951bdfd28dbb7d763e8889a35.jpeg";

// Renders profesionales por familia (Vista 3D)
const RENDERS = {
  zapatas: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/bd2df03624ac96da44f92121320c8d422945fbaac1f7620709528e567583ae73.jpeg",
  viga_cimentacion: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/4ce535e0c00f2b5b1f480af35fb774c944d541f2422c11daa78ac69fa0a0a4d2.jpeg",
  cimiento_corrido: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/4021cfd50ca41bd5d6c2e4cabf3c6b7bd5a46d34aedaa34eb53c53efb5992562.jpeg",
  sobrecimiento: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/d3399e06ddb06fd3f4623913a1358aa4dfcebd42f1d470100cb2bce9a0ba211b.jpeg",
  columnas: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/4a26faf4c99c13f950216218a2976afc31a48a5fe809dde48894b69f723da1d1.jpeg",
  placas: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/e6fd9341e01b5e84e8a8c3fcd13044c5ddaf0fca4531b7939390ac42000fdcb6.jpeg",
  vigas: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/4ce535e0c00f2b5b1f480af35fb774c944d541f2422c11daa78ac69fa0a0a4d2.jpeg",
  techo: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/31b42ad4a6e1cd8847f137bb925df1b896e2c1b1c6102692460adbbc59da494d.jpeg",
  escalera: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/5ec1c27a7c3104cf7cec2ce65eefaf90a52ed8c2b085a209afa8bd4d52606adf.jpeg",
  cisterna: "https://static.prod-images.emergentagent.com/jobs/232128f3-ace6-4dee-aa51-d4f0e57ee90d/images/8e7a4e19422d1a416c6f655e30c97a4ccdad64bd569b410c86123219f4b5f302.jpeg",
};

// Fondos por grupo de columnas (organización visual)
const HDR_DIM = "#eef2f5";   // Dimensiones · gris suave
const HDR_XX = "#fff7ed";    // Acero eje X-X · ámbar suave
const HDR_YY = "#ecfeff";    // Acero eje Y-Y · celeste suave
const HDR_C = "#f0fdf4";     // Concreto · verde suave
const HDR_A = "#fef2f2";     // Acero total · rojo suave
const HDR_MAT = "#f5f3ff";   // Materiales (f'c / fy) · violeta suave

/* ── Widget de Verificación de Control · Presupuesto COREX en tiempo real (con desglose + alerta) ── */
const DEV_THRESHOLD = 0.05;   // 5% de desvío respecto a la cifra base → alerta crítica
const widgetState = (total, enControl, controlTotal) => {
  if (total == null) return "empty";
  const dev = Math.abs(total - (controlTotal || 448287.47)) / (controlTotal || 448287.47);
  if (enControl || dev < 0.0001) return "control";
  return dev > DEV_THRESHOLD ? "critical" : "modified";
};
const WSTATE = {
  empty: { c: "#94a3b8", bg: "#f1f5f9", bd: "#e2e8f0", lbl: "" },
  control: { c: "#16a34a", bg: "#ecfdf5", bd: "#a7f3d0", lbl: "· en control" },
  modified: { c: "#b45309", bg: "#fffbeb", bd: "#fde68a", lbl: "· modificado" },
  critical: { c: "#dc2626", bg: "#fef2f2", bd: "#fecaca", lbl: "· ¡desvío crítico!" },
};
const PieRow = ({ label, value, bold, accent }) => (
  <div className={`flex items-center justify-between ${bold ? "font-extrabold" : ""}`} style={accent ? { color: accent } : {}}>
    <span className={bold ? "" : "text-slate-500"}>{label}</span>
    <span className={bold ? "" : "text-slate-700 font-semibold"}>S/ {n2(value)}</span>
  </div>
);
const CorexBudgetWidget = ({ pie, enControl, controlTotal = 448287.47, syncing, pisos, incidenciaPct }) => {
  const total = pie?.budgetTotal;
  const has = total != null;
  const st = widgetState(total, enControl, controlTotal);
  const C = WSTATE[st];
  const critical = st === "critical";
  const dev = has ? ((total - controlTotal) / controlTotal) * 100 : 0;
  const multinivel = (pisos || 0) > 1;
  const incPct = Number(incidenciaPct || 0);
  return (
    <div className="relative group" data-testid="corex-budget-widget" data-state={st}>
      <div className="flex items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm cursor-help" style={{ background: C.bg, borderColor: C.bd }}>
        <span className="grid place-items-center h-7 w-7 rounded-md text-white shrink-0" style={{ background: C.c }}>
          {syncing ? <Loader2 size={14} className="animate-spin" /> : critical ? <AlertTriangle size={14} /> : <Wallet size={14} />}
        </span>
        <div className="min-w-0 leading-tight">
          <div className="text-[8px] font-bold uppercase tracking-wide" style={{ color: C.c }}>Presupuesto COREX {has && C.lbl}</div>
          <div className="text-[15px] font-extrabold text-slate-800 leading-none" data-testid="corex-budget-total">{has ? `S/ ${n2(total)}` : "S/ —"}</div>
        </div>
        {has && <span className="ml-1 shrink-0" data-testid="corex-budget-badge">{st === "control" ? <Check size={14} style={{ color: C.c }} /> : critical ? <AlertTriangle size={14} style={{ color: C.c }} /> : <Info size={14} style={{ color: C.c }} />}</span>}
      </div>
      {multinivel && (
        <div className="mt-1 flex items-center gap-1 rounded-md px-2 py-0.5 text-[9px] font-bold w-fit"
             style={{ background: incPct > 0 ? "#fef3c7" : "#e0f2fe", color: incPct > 0 ? "#92400e" : "#075985" }}
             data-testid="corex-multinivel-badge">
          <Layers size={10} />
          {incPct > 0 ? `Incidencia por Altura (+${n2(incPct)}%) Activa` : `Multinivel Activo · ${pisos} Pisos`}
        </div>
      )}
      {has && (
        <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-xl border border-slate-200 bg-white shadow-2xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity" data-testid="corex-budget-breakdown">
          <div className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 mb-2">Desglose del presupuesto</div>
          <div className="space-y-1 text-[11px]">
            <PieRow label="Costo Directo (CD)" value={pie.directCost} />
            <PieRow label="Gastos Generales (GG)" value={pie.generalExpenses} />
            {pie.freightTotal ? <PieRow label="Fletes" value={pie.freightTotal} /> : null}
            <PieRow label="Utilidad (10%)" value={pie.utility} />
            <div className="border-t border-slate-100 my-1" />
            <PieRow label="Subtotal" value={pie.subtotal} />
            <PieRow label="IGV (18%)" value={pie.igv} accent="#0ea5b7" />
            <PieRow label="Valor Referencial" value={pie.valorReferencial} />
            <PieRow label="Supervisión (SUP)" value={pie.supervisionCost} />
            <div className="border-t border-slate-200 my-1" />
            <PieRow label="TOTAL" value={total} bold accent={C.c} />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px]">
            <span className="text-slate-400">Base Santisteban</span>
            <span className="font-semibold text-slate-600">S/ {n2(controlTotal)}</span>
          </div>
          <div className="text-[10px] mt-0.5 flex items-center justify-between">
            <span className="text-slate-400">Desvío</span>
            <span className="font-extrabold" style={{ color: C.c }}>{dev >= 0 ? "+" : ""}{n2(dev)}%</span>
          </div>
          {critical && (
            <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2 py-1.5 text-[10px] font-semibold text-red-700" data-testid="corex-budget-alert">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" /> Desvío &gt; {DEV_THRESHOLD * 100}% respecto a la base. Revise la viabilidad económica del proyecto.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Componentes chicos ── */
const FAM_LABEL = Object.fromEntries(MASTER_ORDER.map((f) => [f.key, f.label]));
const showv = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const AuditPanel = ({ items, onClear, onClose }) => (
  <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="vm-est-audit-panel">
    <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ background: PETROL }}>
      <History size={15} /><div className="text-[12px] font-extrabold uppercase tracking-wide">Bitácora de Auditoría · Historial de Cambios</div>
      <span className="ml-auto text-[10px] text-white/60">{items.length} registro(s)</span>
      <button onClick={onClear} data-testid="vm-est-audit-clear" className="rounded-md bg-white/10 hover:bg-white/20 px-2 py-1 text-[10px] font-bold flex items-center gap-1"><Trash2 size={11} /> Limpiar</button>
      <button onClick={onClose} data-testid="vm-est-audit-close" className="rounded-md bg-white/10 hover:bg-white/20 px-2 py-1 text-[10px] font-bold">Cerrar</button>
    </div>
    <div className="max-h-72 overflow-y-auto">
      {items.length === 0 ? (
        <div className="p-4 text-center text-[12px] text-slate-400">Sin cambios registrados · el diseño está en su estado base certificado.</div>
      ) : (
        <table className="w-full text-[11px]">
          <thead><tr className="text-[9px] font-bold uppercase text-slate-500" style={{ background: "#f1f5f9" }}>
            <th className="px-2 py-1 text-left">Fecha</th><th className="px-2 py-1 text-left">Elemento / Campo</th><th className="px-2 py-1.5 text-center">Antes → Después</th><th className="px-2 py-1.5 text-right">Impacto (S/)</th><th className="px-2 py-1.5 text-right">Total (S/)</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 align-top" data-testid="vm-est-audit-row">
                <td className="px-2 py-1 text-slate-400 whitespace-nowrap">{new Date(a.ts).toLocaleString("es-PE")}</td>
                <td className="px-2 py-1 text-slate-700 font-semibold">{(a.cambios || []).map((c, i) => <div key={i}>{c.label}</div>)}</td>
                <td className="px-2 py-1 text-center text-slate-500">{(a.cambios || []).map((c, i) => <div key={i}>{showv(c.de)} → {showv(c.a)}</div>)}</td>
                <td className="px-2 py-1 text-right font-extrabold whitespace-nowrap" style={{ color: a.impacto > 0 ? "#b45309" : a.impacto < 0 ? "#16a34a" : "#64748b" }}>{a.impacto >= 0 ? "+" : ""}{n2(a.impacto)}</td>
                <td className="px-2 py-1 text-right font-semibold text-slate-700 whitespace-nowrap">{n2(a.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </section>
);

const DatoCard = ({ label, value, unit, sub, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-w-0">
    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400 leading-none truncate">{label}</div>
    <div className="mt-1 text-[15px] font-extrabold text-slate-800 leading-none" data-testid={testid}>{value}{unit ? <span className="text-[11px] font-semibold text-slate-400"> {unit}</span> : null}</div>
    {sub ? <div className="text-[9px] text-slate-400 mt-0.5">{sub}</div> : null}
  </div>
);

const DatoInline = ({ label, value, sub, testid }) => (
  <span className="flex items-baseline gap-1 whitespace-nowrap">
    <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-[11px] font-extrabold text-slate-800 leading-none" data-testid={testid}>{value}</span>
    {sub ? <span className="text-[8px] text-slate-400">{sub}</span> : null}
  </span>
);

const ResumenCard = ({ label, value, unit, accent, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-center">
    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400 leading-none">{label}</div>
    <div className="mt-1.5 text-[19px] font-extrabold leading-none" style={{ color: accent || "#0f172a" }} data-testid={testid}>{value}</div>
    {unit ? <div className="text-[9px] text-slate-400 mt-0.5">{unit}</div> : null}
  </div>
);

/* ── Visor SVG paramétrico de zapata ── */
const ZapataSVG = ({ el }) => {
  if (!el) return null;
  const L = Number(el.largo) || 1, A = Number(el.ancho) || 1, H = Number(el.alto) || 0.8;
  return (
    <svg viewBox="0 0 260 220" className="w-full h-[190px]" data-testid="vm-est-visor-3d">
      <defs>
        <linearGradient id="conc" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e2e8f0" /><stop offset="1" stopColor="#94a3b8" />
        </linearGradient>
      </defs>
      {/* base isométrica */}
      <polygon points="40,150 150,120 220,150 110,182" fill="url(#conc)" stroke="#475569" strokeWidth="1.5" />
      <polygon points="40,150 40,168 110,200 110,182" fill="#64748b" stroke="#334155" strokeWidth="1.2" />
      <polygon points="110,182 110,200 220,168 220,150" fill="#7c8ba1" stroke="#334155" strokeWidth="1.2" />
      {/* parrilla de acero */}
      {[0,1,2,3,4].map((i) => (
        <line key={`x${i}`} x1={58 + i*22} y1={148 - i*5} x2={128 + i*22} y2={170 - i*5} stroke="#f59e0b" strokeWidth="1.1" opacity="0.85" />
      ))}
      {[0,1,2,3,4].map((i) => (
        <line key={`y${i}`} x1={60 + i*22} y1={152 + i*3} x2={128 + i*14} y2={135 + i*3} stroke="#0ea5b7" strokeWidth="1.1" opacity="0.7" />
      ))}
      {/* columna */}
      <polygon points="118,60 150,50 168,60 136,72" fill="#cbd5e1" stroke="#475569" strokeWidth="1.2" />
      <polygon points="118,60 118,132 136,144 136,72" fill="#94a3b8" stroke="#334155" strokeWidth="1" />
      <polygon points="136,72 136,144 168,132 168,60" fill="#b6c2d1" stroke="#334155" strokeWidth="1" />
      {/* cotas */}
      <text x="128" y="212" textAnchor="middle" className="fill-slate-500" fontSize="10" fontWeight="700">{n2(L)} m × {n2(A)} m</text>
      <text x="30" y="160" textAnchor="middle" className="fill-slate-400" fontSize="9" fontWeight="700" transform="rotate(-90 30 160)">{n2(H)} m</text>
    </svg>
  );
};

export default function VmEstructural() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const openCorexNucleo = async () => {
    setToast("Sincronizando Oráculo COREX con Inicio…");
    let pid = null;
    try { const { data } = await api.post("/vm/estructural/reconcile-corex"); pid = data?.projectId || null; }
    catch (e) { setToast("No se pudo preparar tu Oráculo COREX. Intenta de nuevo en unos segundos."); return; }
    if (!pid) { setToast("No se pudo preparar tu Oráculo COREX. Intenta de nuevo."); return; }
    try { window.localStorage.setItem("cl_corex_origin", "multifamiliar"); window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: pid, projectName: COREX_V2_PROJECT_NAME, module: "presupuesto-general", lock: false })); } catch (e) { /* noop */ }
    navigate("/software/corex");
  };
  // Aislamiento total (no-admin): abre/crea el Oráculo Sandoval PRIVADO del usuario en modo kiosco.
  const openMiOraculo = async () => {
    try {
      const { data } = await api.post("/corex-workspace/mi-oraculo");
      if (!data?.projectId) return;
      try { window.localStorage.setItem("cl_corex_origin", "multifamiliar"); window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: data.projectId, projectName: data.projectName, module: "presupuesto-general", lock: true })); } catch (e) { /* noop */ }
      navigate("/software/corex");
    } catch (e) { /* CorexPortal reintentará/creará la copia */ navigate("/software/corex"); }
  };
  const [data, setData] = useState(null);
  // P1 · Espejo síncrono del último estado para evitar el race de ediciones rápidas (closure obsoleto).
  const dataRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState("");
  const [familia, setFamilia] = useState("cimentacion");
  const [sub, setSub] = useState("zapatas");
  const [selId, setSelId] = useState("z-01");
  const [detailTab, setDetailTab] = useState("3d");
  const [nivel, setNivel] = useState("todos");
  const [calcBusy, setCalcBusy] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [calcRes, setCalcRes] = useState(null);
  const [vista, setVista] = useState("familias");
  const [selFamily, setSelFamily] = useState("zapatas");
  const [corexSync, setCorexSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const pendingEdits = useRef([]);
  const totalBeforeRef = useRef(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2600); };

  // Gate BÁSICO (Fase A): la BD Estructural requiere Pro/Premium. Admin conserva
  // acceso para gestión del catálogo estructural.
  const { features, loading: planLoading } = usePlanFeatures();
  useEffect(() => {
    if (!planLoading && !features.bd_estructural && !isAdmin) {
      navigate("/planes", { replace: true });
    }
  }, [planLoading, features.bd_estructural, isAdmin, navigate]);

  const changeFamilia = (fk) => { setFamilia(fk); setSub(FAM_SUBS[fk][0].key); };

  const fetchAudit = useCallback(async () => {
    try { const { data } = await api.get("/vm/estructural/audit"); setAuditLog(data.items || []); }
    catch (e) { /* noop */ }
  }, []);

  // Puente Maestro: sincroniza las 10 familias con el motor COREX (Delta-Baseline) y actualiza el Widget.
  const syncOverride = useCallback(async () => {
    try {
      setSyncing(true);
      const res = await runRecalc();   // async P0: 202 + jobId + polling hasta estado terminal
      if (res.status !== "COMPLETED") return;   // ERROR/TIMEOUT: el widget conserva el último valor
      setCorexSync({ total: res.total, enControl: null });
      window.dispatchEvent(new Event("corex-refresh"));
      if (pendingEdits.current.length) {   // registrar en la bitácora el impacto exacto del cambio
        const after = res.total?.budgetTotal;
        const before = totalBeforeRef.current;
        const impacto = (before != null && after != null) ? Number((after - before).toFixed(2)) : 0;
        const entry = { cambios: pendingEdits.current.slice(), impacto, total: after, enControl: null };
        pendingEdits.current = []; totalBeforeRef.current = null;
        try { await api.post("/vm/estructural/audit", entry); await fetchAudit(); } catch (e) { /* noop */ }
      }
    } catch (e) { /* silencioso: el widget conserva el último valor */ }
    finally { setSyncing(false); }
  }, [isAdmin, fetchAudit]);

  // Delta por familia (para la Memoria PDF) a partir del último override.
  const familyDelta = useCallback((familyKey) => {
    const rows = (corexSync?.cambios || []).filter((c) => c.familia === familyKey);
    const g = (t) => rows.find((r) => r.tipo === t)?.delta || 0;
    return { concreto: g("concreto"), acero: g("acero") };
  }, [corexSync]);

  const calcularProyecto = async () => {
    setCalcBusy(true);
    try { const { data: r } = await api.post("/vm/estructural/calcular-proyecto"); setCalcRes(r); flash(r.mensaje); }
    catch (e) { setErr(humanizeAxiosError(e)); flash("No se pudo sincronizar el proyecto."); }
    finally { setCalcBusy(false); }
  };

  // Nuevo/Restaurar: devuelve Inicio + BD Estructuras al estado oficial del Excel y recalcula.
  const restaurarOficial = async () => {
    if (!window.confirm("¿Restaurar todos los datos al estado oficial? Esta acción sobreescribirá sus ediciones.")) return;
    setRestaurando(true);
    try {
      await api.post("/vm/estructural/restore-oficial");   // el backend dispara el puente automáticamente
      const { data } = await api.get("/vm/estructural"); setData(data);
      await syncOverride();
      flash("Datos restaurados al estado oficial del Excel. Presupuesto recalculado.");
    } catch (e) { setErr(humanizeAxiosError(e)); flash("No se pudo restaurar al estado oficial."); }
    finally { setRestaurando(false); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/vm/estructural"); setData(data); }
      catch (e) { setErr(humanizeAxiosError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (loading || !data) return;
    (async () => {
      try { const { data: t } = await api.get("/vm/estructural/corex-total"); setCorexSync({ total: t, enControl: t.enControl, controlTotal: t.controlTotal }); }
      catch (e) { /* noop */ }
      syncOverride();   // refina con el recálculo completo del Puente Maestro
    })();
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => { if (isAdmin) fetchAudit(); }, [isAdmin, fetchAudit]);

  const manual = data?.modo === "MANUAL";
  const ctx = data?.contexto || {};
  const resumenes = data?.resumenes || {};
  const familiasData = data?.familias || {};

  const zapatas = familiasData.zapatas || [];

  /* Edición genérica de cualquier familia (dims/cantidad/acero); los resultados (concreto/acero) los recalcula el backend */
  const setElField = (familyKey, id, path, value) => {
    // P1: partir SIEMPRE del último estado (dataRef), no del closure 'data' que puede estar desfasado
    // en ediciones rápidas consecutivas → así ninguna edición intermedia se pierde.
    const next = structuredClone(dataRef.current || data);
    const arr = next.familias[familyKey] || [];
    const el = arr.find((r) => r.id === id);
    if (!el) return;
    let oldVal;
    if (path.includes(".")) { const [a, b] = path.split("."); oldVal = el[a]?.[b]; el[a] = el[a] || {}; el[a][b] = value; }
    else { oldVal = el[path]; el[path] = value; }
    // Fórmula editable con fallback: marcar/desmarcar override manual del path (vacío/0 → vuelve a fórmula)
    if ((el.formulaFields || []).includes(path)) {
      const m = new Set(el.manual || []);
      const nv = typeof value === "number" ? value : parseFloat(value);
      if (!nv || isNaN(nv) || nv <= 0) m.delete(path); else m.add(path);
      el.manual = Array.from(m);
    }
    if (isAdmin && String(oldVal) !== String(value)) {   // registrar edición para la bitácora
      if (pendingEdits.current.length === 0) totalBeforeRef.current = corexSync?.total?.budgetTotal ?? null;
      pendingEdits.current.push({ label: `${FAM_LABEL[familyKey] || familyKey} · ${el.codigo || id} · ${path}`, de: oldVal, a: value });
    }
    scheduleSave(next);
  };

  // P1 · Mantener el espejo alineado con cada setData (cargas del servidor, fresh del persist).
  const dataRef2Sync = data;
  useEffect(() => { dataRef.current = dataRef2Sync; }, [dataRef2Sync]);

  const subRows = familiasData[sub] || [];
  const selEl = useMemo(() => subRows.find((r) => r.id === selId) || subRows[0] || null, [subRows, selId]);
  const changeSub = (k) => { setSub(k); const rows = familiasData[k] || []; setSelId(rows[0]?.id || null); };
  const selElGlobal = useMemo(() => {
    for (const k of Object.keys(familiasData)) {
      const arr = familiasData[k];
      if (Array.isArray(arr)) { const f = arr.find((r) => r.id === selId); if (f) return f; }
    }
    return null;
  }, [familiasData, selId]);
  const pickElement = (fk, id) => { setSelFamily(fk); setSelId(id); };

  /* Guardar (PUT) → devuelve resultados recalculados por el backend */
  const persist = useCallback(async (next, mySeq) => {
    try {
      setSaving(true);
      const strip = (arr) => (arr || []).map(({ concreto, acero, formulaFields, ...r }) => r);
      const body = {
        modo: next.modo,
        params: next.params,
        zapatas: strip(next.familias.zapatas),
        viga_cimentacion: strip(next.familias.viga_cimentacion),
        cimiento_corrido: strip(next.familias.cimiento_corrido),
        sobrecimiento: strip(next.familias.sobrecimiento),
        columnas: strip(next.familias.columnas),
        placas: strip(next.familias.placas),
        vigas: strip(next.familias.vigas),
        techo: strip(next.familias.techo),
        escalera: strip(next.familias.escalera),
        cisterna: strip(next.familias.cisterna),
        luz_columnas: next.familias.luz_columnas,
      };
      const { data: fresh } = await api.put("/vm/estructural", body);
      editState.applyFresh = setData;
      // P2: aplica la respuesta SOLO si no hubo una EDICIÓN más nueva desde que se emitió este guardado
      // (mySeq = secuencia de la edición). Así una respuesta vieja nunca pisa un estado más nuevo.
      if (mySeq === editState.seq) {
        if (editState.active > 0) { editState.pendingFresh = fresh; editState.pendingSeq = mySeq; }
        else setData(fresh);
      }
      setSaved(true); setTimeout(() => setSaved(false), 1500);
      syncOverride();   // el diseño gobierna el presupuesto: recalcula el total tras cada cambio
    } catch (e) { setErr(humanizeAxiosError(e)); }
    finally { setSaving(false); }
  }, [syncOverride]);

  const debounce = useRef(null);
  const scheduleSave = useCallback((next) => {
    const mySeq = ++editState.seq;   // P2: secuencia por EDICIÓN (no por PUT) → gobierna qué respuesta es válida
    dataRef.current = next;   // P1: sincroniza el último estado ANTES de re-render (edición rápida OK)
    setData(next);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => persist(next, mySeq), 800);
  }, [persist]);

  const toggleModo = () => {
    const next = structuredClone(data);
    next.modo = manual ? "AUTOMATICO" : "MANUAL";
    scheduleSave(next);
    flash(next.modo === "MANUAL" ? "Modo MANUAL activo." : "Modo AUTOMÁTICO: el motor recalcula todo.");
  };

  if (loading) return <div className="h-full grid place-items-center bg-[#eef2f5]"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (!data) return <div className="h-full grid place-items-center bg-[#eef2f5] text-slate-500">{err || "No se pudo cargar"}</div>;

  const nivelesDisp = ctx.pisos ? ["todos", ...Array.from({ length: ctx.pisos }, (_, i) => String(i + 1))] : ["todos"];

  return (
    <div className="h-full flex text-slate-800 overflow-hidden" style={{ background: "#eef2f5" }} data-testid="vm-estructural">
      {/* Sidebar oficial (no se duplica) */}
      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-56"} shrink-0 flex-col text-white transition-all duration-200`} style={{ background: PETROL }}>
        <div className={`border-b border-white/10 flex items-center justify-center bg-white ${collapsed ? "px-1 py-2" : "px-2.5 py-3"}`}>
          <img src="/corex-sidebar-logo.webp" alt="COREX Multifamiliar" data-testid="vm-est-sidebar-logo" className="w-full h-auto object-contain" />
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_GROUPS.map((grp) => {
            const items = grp.items.filter((it) => (!it.adminOnly || isAdmin) && (!it.clientOnly || !isAdmin));
            if (!items.length) return null;
            return (
            <div key={grp.label} className="mb-1">
              {!collapsed && <div className="px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/35">{grp.label}</div>}
              {items.map((it) => {
                const active = it.key === "base-estructural";
                return (
                  <button key={it.key} data-testid={`vm-est-nav-${it.key}`}
                    onClick={() => { if (active) return; if (it.key === "mi-presupuesto") { openMiOraculo(); return; } if (it.key === "corex-nucleo") { openCorexNucleo(); return; } if (it.route) navigate(it.route); else flash("Módulo disponible en el Workspace COREX · abre desde Inicio."); }}
                    title={it.label}
                    className="group relative w-full flex items-center gap-2.5 px-3 py-2 text-[12.5px] font-semibold transition-colors"
                    style={active ? { background: PETROL_2, color: "#fff" } : { color: "rgba(255,255,255,0.72)" }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = PETROL_3; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                    {active && <span className="absolute left-0 top-0 h-full w-1" style={{ background: NEON }} />}
                    <it.icon size={16} style={{ color: active ? NEON : "inherit" }} className="shrink-0" />
                    {!collapsed && <span className="flex-1 text-left leading-tight break-words">{it.label}</span>}
                  </button>
                );
              })}
            </div>
            );
          })}
        </nav>
        <button onClick={() => setCollapsed((v) => !v)} data-testid="vm-est-collapse"
          className="flex items-center justify-center gap-1.5 border-t border-white/10 px-3 py-2.5 text-[11px] font-semibold text-white/60 hover:bg-white/5 transition-colors">
          <ChevronLeft size={14} className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />{!collapsed && "Contraer menú"}
        </button>
      </aside>

      {/* Columna principal */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] border-b border-slate-200 bg-white/70">
          <Home size={12} className="text-emerald-500" />
          <span className="font-bold text-slate-700">Bases de Ingeniería</span>
          <span className="text-slate-300">/</span>
          <span className="text-slate-500">Base de Datos Estructural</span>
          <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold">
            {saving ? <span className="flex items-center gap-1 text-slate-400"><Loader2 size={11} className="animate-spin" /> Guardando…</span>
              : saved ? <span className="flex items-center gap-1 text-emerald-600"><Check size={11} /> Guardado</span>
              : <span className="flex items-center gap-1 text-slate-400"><Save size={11} /> Autoguardado activo</span>}
          </span>
        </div>

        {/* Encabezado del módulo */}
        <header className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-slate-200 shrink-0">
          <span className="grid place-items-center h-9 w-9 rounded-lg text-white" style={{ background: PETROL }}><Landmark size={18} /></span>
          <div className="min-w-0">
            <div className="text-[15px] font-extrabold text-slate-800 leading-tight">BASE DE DATOS ESTRUCTURAL</div>
            <div className="text-[10px] text-slate-400 leading-tight">Parámetros estructurales paramétricos para metrados y presupuesto</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && <CorexBudgetWidget pie={corexSync?.total} enControl={corexSync?.enControl} controlTotal={corexSync?.controlTotal} syncing={syncing} pisos={ctx.pisos} incidenciaPct={({ 3: 3, 4: 6, 5: 9 })[ctx.pisos] || 0} />}
            {ctx.sincronizado
              ? <span className="flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] font-bold text-emerald-700" data-testid="vm-est-sync"><Check size={13} /> Sincronizado con Inicio</span>
              : <span className="flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] font-bold text-amber-700" data-testid="vm-est-sync"><Info size={13} /> Sin datos de Inicio</span>}
            {isAdmin && (
              <button onClick={() => setAuditOpen((v) => !v)} data-testid="vm-est-audit-toggle"
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 transition-colors">
                <History size={13} /> Bitácora {auditLog.length > 0 && <span className="rounded-full bg-slate-800 text-white text-[9px] px-1.5 py-0.5">{auditLog.length}</span>}
              </button>
            )}
            <button onClick={restaurarOficial} disabled={restaurando} data-testid="vm-est-restaurar-oficial"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">
              {restaurando ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Nuevo/Restaurar
            </button>
            <button onClick={calcularProyecto} disabled={calcBusy} data-testid="vm-est-calcular-proyecto"
              className="cl-metallic flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-extrabold disabled:opacity-60">
              {calcBusy ? <Loader2 size={14} className="animate-spin" /> : <Sigma size={14} />} CALCULAR EL PROYECTO
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isAdmin && auditOpen && (
            <AuditPanel items={auditLog}
              onClose={() => setAuditOpen(false)}
              onClear={async () => { try { await api.delete("/vm/estructural/audit"); setAuditLog([]); } catch (e) {} }} />
          )}
          {/* DATOS DEL PROYECTO (heredados) — franja única densa */}
          <section className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5" data-testid="vm-est-datos-proyecto">
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500">Datos del Proyecto</span>
            <span className="text-[9px] text-slate-300">·</span>
            <DatoInline label="Ancho" value={`${n2(ctx.ancho)} m`} testid="vm-est-ancho" />
            <DatoInline label="Largo" value={`${n2(ctx.largo)} m`} testid="vm-est-largo" />
            <DatoInline label="Pisos" value={ctx.pisos ?? "—"} sub={ctx.modoNiveles === "ESPECIFICO" ? "esp." : "acum."} testid="vm-est-pisos" />
            <DatoInline label="H 1er piso" value={`${n2(ctx.altura1)} m`} testid="vm-est-altura1" />
            <DatoInline label="Parapeto" value={`${n2(ctx.alturaParapeto)} m`} testid="vm-est-parapeto" />
            <span className="ml-auto flex items-center gap-1 text-[9px] text-slate-300"><Info size={10} /> solo lectura</span>
            <button onClick={() => navigate("/software/vivienda-multifamiliar")} data-testid="vm-est-ir-inicio"
              className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              <ExternalLink size={11} /> Inicio
            </button>
          </section>

          {/* Modo de diseño + Parámetros + Nivel */}
          <section className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
              <span className="text-[10px] font-bold uppercase text-slate-400">Modo de diseño:</span>
              <button onClick={toggleModo} data-testid="vm-est-modo"
                className="flex items-center gap-1.5 text-[12px] font-extrabold" style={{ color: manual ? "#b45309" : TEAL }}>
                {manual ? "Manual" : "Automático"} <ChevronDown size={13} />
              </button>
            </div>
            <button data-testid="vm-est-parametros" onClick={() => flash(`Parámetros generales · f'c ${n2(data.params.fc)} kg/cm² · fy ${n2(data.params.fy)} kg/cm² · recubrimiento ${n2(data.params.recubrimiento*100)} cm`)}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              <Settings size={13} /> Parámetros generales
            </button>
            {/* P2 · Cuadro "Luz entre columnas" (solo lectura, se actualiza desde Inicio: X-X = Ancho/2) */}
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5" data-testid="vm-est-luz-panel">
              <span className="text-[10px] font-bold uppercase text-slate-400">Luz entre columnas</span>
              <span className="flex items-center gap-1 text-[11px]"><span className="text-slate-400">Y-Y (Largo):</span><b className="text-slate-700" data-testid="vm-est-luz-yy">{n2((familiasData.luz_columnas || {}).separacionYY ?? 4)} m</b></span>
              <span className="flex items-center gap-1 text-[11px]"><span className="text-slate-400">X-X (Ancho):</span><b className="text-slate-700" data-testid="vm-est-luz-xx">{n2((Number(ctx.ancho) || 0) / 2)} m</b></span>
              <span className="flex items-center gap-1 text-[9px] text-slate-300"><Info size={10} /> auto · lee Inicio</span>
            </div>
            <div className="ml-auto flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
              <span className="text-[10px] font-bold uppercase text-slate-400">Nivel:</span>
              <select value={nivel} onChange={(e) => setNivel(e.target.value)} data-testid="vm-est-nivel"
                className="text-[12px] font-semibold text-slate-700 bg-transparent outline-none">
                {nivelesDisp.map((nv) => <option key={nv} value={nv}>{nv === "todos" ? "Todos los niveles" : `Piso ${nv}`}</option>)}
              </select>
            </div>
          </section>

          {/* Familias + Resumen de Elementos */}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="vm-est-familias">
            <button data-testid="vm-est-resumen-elementos" onClick={() => setVista("resumen")}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-extrabold transition-colors"
              style={vista === "resumen" ? { background: "linear-gradient(135deg,#0ea5b7,#16a34a)", color: "#fff" } : { background: "#fff", color: NEON, border: `1px solid ${NEON}` }}>
              <Boxes size={15} /> RESUMEN DE ELEMENTOS
            </button>
            {FAMILIAS.map((f) => {
              const active = f.key === familia && vista === "familias";
              const fc = FAM_COLOR[f.key] || FAM_COLOR.otros;
              return (
                <button key={f.key} data-testid={`vm-est-fam-${f.key}`} onClick={() => { setVista("familias"); changeFamilia(f.key); }}
                  className="flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-bold transition-colors"
                  style={active ? { background: fc.c, color: "#fff", boxShadow: `0 4px 12px ${fc.c}55` } : { background: fc.soft, color: fc.c, border: `1px solid ${fc.bd}` }}>
                  <f.icon size={15} style={{ color: active ? "#fff" : fc.c }} /> {f.label}
                </button>
              );
            })}
          </div>

          {vista === "resumen" ? (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_330px] gap-3" data-testid="vm-est-resumen-view">
              <div className="min-w-0 space-y-3" data-testid="vm-est-master-view">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700 flex items-center gap-1.5">
                  <Check size={13} /> Vista Maestra de Edición Total · las 10 familias son editables y persistentes · el render 3D sigue el elemento seleccionado.
                </div>
                {MASTER_ORDER.map((fam) => {
                  const familyKey = fam.key;
                  const rows = familiasData[familyKey] || [];
                  const kind = KIND[familyKey] || "grid";
                  const tableProps = {
                    familyKey, rows, resumen: resumenes[familyKey], label: fam.label, params: data.params,
                    selId: (selFamily === familyKey ? selId : null), onSel: (id) => pickElement(familyKey, id), onEdit: setElField,
                    luz: familyKey === "columnas" ? familiasData.luz_columnas : null,
                  };
                  return (
                    <div key={familyKey} data-testid={`vm-est-master-section-${familyKey}`}>
                      {kind === "grid" && <GridStructTable {...tableProps} />}
                      {kind === "long" && <RebarLongTable {...tableProps} />}
                      {kind === "techo" && <TechoTable {...tableProps} />}
                      {kind === "plain" && <PlainTable {...tableProps} />}
                    </div>
                  );
                })}
              </div>
              <DetailPanel el={selElGlobal} familyKey={selFamily} params={data.params} detailTab={detailTab} setDetailTab={setDetailTab}
                onExport={() => { if (selElGlobal) generarMemoriaCalculo({ el: selElGlobal, familyKey: selFamily, params: data.params, ctx, delta: familyDelta(selFamily), historial: auditLog, resumen: { controlTotal: corexSync?.controlTotal || 448287.47, total: corexSync?.total?.budgetTotal } }); }} />
            </div>
          ) : (() => {
            const subs = FAM_SUBS[familia];
            const meta = subs.find((s) => s.key === sub) || subs[0];
            const familyKey = meta.key;
            const rows = familiasData[familyKey] || [];
            const kind = KIND[familyKey] || "grid";
            const tableProps = {
              familyKey, rows, resumen: resumenes[familyKey], label: meta.label, params: data.params,
              selId: selEl?.id, onSel: setSelId, onEdit: setElField,
              luz: familyKey === "columnas" ? familiasData.luz_columnas : null,
            };
            return (
              <div className="space-y-3" data-testid={`vm-est-familia-${familia}`}>
                {/* Subnav de la familia */}
                <div className="flex flex-wrap gap-1.5" data-testid={`vm-est-${familia}-subnav`}>
                  {subs.map((s) => {
                    const active = s.key === sub;
                    return (
                      <button key={s.key} data-testid={`vm-est-sub-${s.key}`} onClick={() => changeSub(s.key)}
                        className="rounded-lg px-3 py-1.5 text-[11.5px] font-bold transition-colors"
                        style={active ? { background: TEAL, color: "#fff" } : { background: "#eef2f5", color: "#64748b" }}>
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[1fr_330px] gap-3">
                  <div className="min-w-0 space-y-3">
                    {kind === "grid" && <GridStructTable {...tableProps} />}
                    {kind === "long" && <RebarLongTable {...tableProps} />}
                    {kind === "techo" && <TechoTable {...tableProps} />}
                    {kind === "plain" && <PlainTable {...tableProps} />}

                    {familyKey === "techo" && <LosaAligeradaMotor ctx={ctx} el={rows[0]} onEdit={setElField} params={data.params} />}
                    {familyKey === "placas" && <PlacasMotor ctx={ctx} rows={rows} params={data.params} />}

                    <div className="space-y-1.5">
                      {subs.filter((s) => s.key !== sub).map((s) => {
                        const r = resumenes[s.key] || {};
                        return (
                          <button key={s.key} onClick={() => changeSub(s.key)} data-testid={`vm-est-compact-${s.key}`}
                            className="w-full flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-slate-300 transition-colors">
                            <Landmark size={15} className="text-slate-400 shrink-0" />
                            <span className="text-[12px] font-bold text-slate-700">{s.label}</span>
                            <span className="text-[11px] text-slate-500">{r.tipos} tipo{r.tipos === 1 ? "" : "s"}</span>
                            {r.volumenConcreto ? <span className="text-[11px] text-slate-400">· Concreto: {n2(r.volumenConcreto)} m³</span> : null}
                            {r.pesoAcero ? <span className="text-[11px] text-slate-400">· Acero: {n2(r.pesoAcero)} kg</span> : null}
                            <span className="ml-auto flex items-center gap-1 text-[11px] font-bold" style={{ color: TEAL }}>VER <ChevronDown size={12} className="-rotate-90" /></span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Panel técnico del elemento seleccionado */}
                  <DetailPanel el={selEl} familyKey={familyKey} params={data.params} detailTab={detailTab} setDetailTab={setDetailTab}
                    onExport={() => { if (selEl) generarMemoriaCalculo({ el: selEl, familyKey, params: data.params, ctx, delta: familyDelta(familyKey), historial: auditLog, resumen: { controlTotal: corexSync?.controlTotal || 448287.47, total: corexSync?.total?.budgetTotal } }); }} />
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {toast && <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-slate-900 text-white text-[12px] font-semibold px-4 py-2 shadow-lg" data-testid="vm-est-toast">{toast}</div>}

      {calcRes && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setCalcRes(null)} data-testid="vm-est-calc-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: "linear-gradient(135deg,#0ea5b7,#16a34a)" }}>
              <Check size={18} /><div className="text-[14px] font-extrabold">Proyecto sincronizado con Metrados y Presupuesto</div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <ResumenCard label="Partidas" value={calcRes.totales.partidas} unit="generadas" />
                <ResumenCard label="Concreto total" value={n2(calcRes.totales.concreto_m3)} unit="m³" accent={TEAL} />
                <ResumenCard label="Acero total" value={n2(calcRes.totales.acero_kg)} unit="kg" accent="#b45309" />
              </div>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-[11px]">
                  <thead><tr className="text-[9px] font-bold uppercase text-slate-500" style={{ background: "#f1f5f9" }}>
                    <th className="px-2 py-1 text-left">Capítulo</th><th className="px-2 py-1 text-left">Partida</th><th className="px-2 py-1.5 text-right">Metrado</th><th className="px-2 py-1.5 text-center">Und</th>
                  </tr></thead>
                  <tbody>
                    {calcRes.partidas.map((p, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 text-slate-400">{p.capitulo}</td>
                        <td className="px-2 py-1 font-semibold text-slate-700">{p.descripcion}</td>
                        <td className="px-2 py-1 text-right font-extrabold text-slate-800">{n2(p.metrado)}</td>
                        <td className="px-2 py-1 text-center text-slate-400">{p.unidad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => setCalcRes(null)} data-testid="vm-est-calc-close" className="mt-3 w-full rounded-lg bg-slate-900 text-white text-[12px] font-bold py-2 hover:bg-slate-800 transition-colors">Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Tabla de Zapatas ── */
/* ── Celdas editables compartidas · dims/cantidad/acero editables; resultados (concreto/acero) read-only ── */
const CELL = "px-1.5 py-0.5 text-center text-[11px]";
const endEditFocus = () => {
  editState.active = Math.max(0, editState.active - 1);
  if (editState.active === 0 && editState.pendingFresh) {
    const f = editState.pendingFresh, s = editState.pendingSeq;
    editState.pendingFresh = null;
    // P2: solo aplica si sigue siendo la respuesta MÁS NUEVA (evita que una respuesta vieja pise estado nuevo)
    if (s === editState.seq && editState.applyFresh) editState.applyFresh(f);
  }
};
const EditNum = ({ v, fk, id, path, onEdit, w = "w-14", step = "0.01" }) => {
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const commit = () => { onEdit(fk, id, path, parseFloat(draft) || 0); setFocused(false); };
  return (
    <input type="text" inputMode="decimal"
      data-testid={`vm-est-in-${id}-${path.replace(".", "-")}`}
      value={focused ? draft : n2(v)}
      onFocus={(e) => { editState.active++; const iv = String(v ?? 0); setFocused(true); setDraft(iv); const t = e.target; setTimeout(() => { if (t.value === iv) t.select(); }, 0); }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { commit(); endEditFocus(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); e.target.blur(); } }}
      onClick={(e) => e.stopPropagation()}
      className={`${w} rounded bg-white border border-slate-200 px-1 py-0.5 text-center text-[11px] font-semibold text-slate-700 outline-none focus:border-teal-400 focus:bg-teal-50/40`} />
  );
};
const EditFierro = ({ v, fk, id, path, onEdit }) => (
  <select value={v || "1/2\""} onChange={(e) => onEdit(fk, id, path, e.target.value)} onClick={(e) => e.stopPropagation()}
    className="rounded bg-white border border-slate-200 px-1 py-0.5 text-[11px] font-semibold text-slate-700 outline-none focus:border-teal-400">
    {FIERROS.map((f) => <option key={f} value={f}>{f}</option>)}
  </select>
);
const EditTxt = ({ v, fk, id, path, onEdit, w = "w-16" }) => (
  <input value={v || ""} onChange={(e) => onEdit(fk, id, path, e.target.value)} onClick={(e) => e.stopPropagation()}
    className={`${w} rounded bg-white border border-slate-200 px-1 py-0.5 text-center text-[10px] text-slate-500 outline-none focus:border-teal-400`} />
);
// Distribución: numérico puro 2 decimales (parsea valores antiguos tipo "@ 0.25 m")
const distNum = (d) => { if (typeof d === "number") return d; const m = String(d ?? "").match(/[\d.]+/); return m ? parseFloat(m[0]) : 0; };
const EditDist = ({ v, fk, id, path, onEdit }) => {
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const num = distNum(v);
  const commit = () => { onEdit(fk, id, path, parseFloat(draft) || 0); setFocused(false); };
  return (
    <input type="text" inputMode="decimal" data-testid={`vm-est-in-${id}-${path.replace(".", "-")}`}
      value={focused ? draft : n2(num)}
      onFocus={(e) => { editState.active++; const iv = String(num); setFocused(true); setDraft(iv); const t = e.target; setTimeout(() => { if (t.value === iv) t.select(); }, 0); }}
      onChange={(e) => setDraft(e.target.value)} onBlur={() => { commit(); endEditFocus(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); e.target.blur(); } }}
      onClick={(e) => e.stopPropagation()}
      className="w-14 rounded bg-white border border-slate-200 px-1 py-0.5 text-center text-[11px] font-semibold text-slate-700 outline-none focus:border-teal-400 focus:bg-teal-50/40" />
  );
};

/* Resumen de familia · franja técnica horizontal ultra-compacta */
const MacroCard = ({ icon: Icon, label, value, unit, color, testid }) => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 border-l-2" style={{ borderLeftColor: color }}>
    <Icon size={13} style={{ color }} className="shrink-0" />
    <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 whitespace-nowrap">{label}</span>
    <span className="text-[12px] font-extrabold text-slate-800 leading-none" data-testid={testid}>{value}{unit ? <span className="text-[9px] font-bold text-slate-400"> {unit}</span> : null}</span>
  </div>
);
const FamResumen = ({ resumen, familyKey }) => {
  const z = familyKey === "zapatas";
  const fc = famColorOf(familyKey);
  return (
    <div className="border-t flex flex-wrap items-center gap-x-1 gap-y-0.5 px-2 py-1" style={{ borderColor: fc.bd, background: fc.soft }}>
      <MacroCard icon={Table2} label="Tipos" value={resumen?.tipos ?? 0} unit="reg." color={fc.c} testid={z ? "vm-est-res-tipos" : undefined} />
      <MacroCard icon={Ruler} label="Cantidad" value={n2(resumen?.cantidadTotal)} unit="und" color="#0891B2" testid={z ? "vm-est-res-cant" : undefined} />
      <MacroCard icon={Boxes} label="Concreto" value={n2(resumen?.volumenConcreto)} unit="m³" color={TEAL} testid={z ? "vm-est-res-concreto" : undefined} />
      <MacroCard icon={Sigma} label="Acero" value={n2(resumen?.pesoAcero)} unit="kg" color="#b45309" testid={z ? "vm-est-res-acero" : undefined} />
    </div>
  );
};

const TableShell = ({ label, luz, children, testid, familyKey, count }) => {
  const fc = famColorOf(familyKey);
  const Icon = famIconOf(familyKey);
  return (
  <div className="cl-deep-card rounded-xl border border-slate-200 overflow-hidden bg-white shadow-2xl border-l-4" style={{ borderLeftColor: fc.c }}>
    <div className="flex items-center gap-2 px-3 py-1.5 text-white" style={{ background: `linear-gradient(90deg, ${fc.c} 0%, ${fc.c}cc 60%, ${fc.c}99 100%)` }}>
      <span className="grid place-items-center h-6 w-6 rounded-md bg-white/25 shrink-0"><Icon size={14} /></span>
      <div className="text-[12px] font-extrabold uppercase tracking-wide drop-shadow-sm">{label}</div>
      {luz ? <span className="ml-auto text-[9.5px] text-white/90" data-testid="vm-est-luz-columnas">Luz Y-Y: <b>{n2(luz.separacionYY)} m</b> · X-X: <b>{n2(luz.separacionXX)} m</b></span> : null}
      {count != null ? <span className={`${luz ? "ml-2" : "ml-auto"} rounded-full bg-white/25 px-2 py-0.5 text-[9px] font-extrabold shrink-0`}>{count} TIPOS</span> : null}
    </div>
    <div className="overflow-x-auto"><table className="w-full border-collapse" data-testid={testid}>{children}</table></div>
  </div>
  );
};

const RowSelect = ({ active, onClick, testid, children, accent = "#0ea5b7" }) => (
  <tr data-testid={testid} onClick={onClick} className="cl-row-glow border-t border-slate-100 cursor-pointer"
    style={active ? { background: `${accent}1A`, boxShadow: `inset 3px 0 0 ${accent}` } : {}}>{children}</tr>
);


/* ── Panel derecho técnico ── */
const DetailPanel = ({ el, familyKey, params, detailTab, setDetailTab, onExport }) => {
  const [opacity, setOpacity] = useState(100);
  if (!el) return <aside className="rounded-xl border border-slate-200 bg-white p-6 text-center text-[12px] text-slate-400 self-start shadow-2xl" data-testid="vm-est-detail">Seleccione un elemento</aside>;
  const isGrid = !!el.aceroXX, isLong = !!el.esquina, isTecho = !!el.positivo;
  const fc = famColorOf(familyKey);
  const tipoLabel = ELEMENTO_TIPO[familyKey] || "Elemento";
  const vista = VISTA_LABEL[detailTab] || "Vista 3D";
  return (
    <aside className="rounded-xl border border-slate-200 bg-white overflow-hidden self-start xl:sticky xl:top-2 shadow-2xl border-l-4" style={{ borderLeftColor: fc.c }} data-testid="vm-est-detail">
      <div className="flex items-center gap-2 px-3 py-1.5 text-white" style={{ background: PETROL }}>
        <Maximize2 size={13} className="shrink-0" style={{ color: NEON }} />
        <div className="text-[11px] font-extrabold uppercase tracking-wide truncate">Detalle 3D del elemento</div>
      </div>
      <div className="px-2 pt-2">
        <div className="flex items-center gap-2 rounded-lg px-2 py-1" style={{ background: fc.soft, border: `1px solid ${fc.bd}` }}>
          <span className="text-[12px] font-extrabold" style={{ color: fc.c }}>{el.codigo} · {tipoLabel.toUpperCase()}</span>
          <span className="ml-auto rounded bg-white px-1.5 py-0.5 text-[9px] font-extrabold border" style={{ color: fc.c, borderColor: fc.bd }}>{el.ubicacion}</span>
        </div>
      </div>
      <div className="p-2">
        <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-gradient-to-b from-slate-800 to-slate-900" data-testid="vm-est-visor">
          <img src={RENDERS[familyKey] || ACERO_ZAPATA_IMG} alt={`Render ${el.codigo}`} style={{ opacity: opacity / 100, transition: "opacity .2s ease-out" }} className="w-full block" data-testid="vm-est-visor-img" />
          <span className="absolute top-1.5 left-1.5 rounded bg-black/45 px-1.5 py-0.5 text-[9px] font-bold text-white" data-testid="vm-est-visor-vista">{vista}</span>
        </div>
        {/* Modo de vista */}
        <div className="mt-1.5 grid grid-cols-4 gap-1">
          {VISTAS.map(([k, lbl]) => {
            const on = detailTab === k;
            return (
              <button key={k} data-testid={`vm-est-vista-${k}`} onClick={() => setDetailTab(k)}
                className="rounded-md py-1 text-[10px] font-bold transition-colors"
                style={on ? { background: fc.c, color: "#fff", boxShadow: `0 3px 10px ${fc.c}55` } : { background: "#f1f5f9", color: "#64748b" }}>{lbl}</button>
            );
          })}
        </div>
        {/* Transparencia */}
        <div className="mt-1.5 flex items-center gap-2">
          <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-slate-500"><Eye size={12} /> Transparencia</span>
          <input type="range" min="20" max="100" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))}
            data-testid="vm-est-transparencia" className="flex-1 accent-current" style={{ color: fc.c }} />
          <span className="text-[10px] font-extrabold w-9 text-right" style={{ color: fc.c }} data-testid="vm-est-transparencia-val">{100 - opacity}%</span>
        </div>
      </div>
      {/* Información técnica */}
      <div className="border-t border-slate-100 p-2">
        <div className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 mb-1">Información técnica</div>
        <table className="w-full text-[10.5px]">
          <tbody className="[&_td]:py-0.5">
            <tr><td className="text-slate-400">Elemento:</td><td className="text-right font-semibold text-slate-700">{tipoLabel}</td></tr>
            <tr><td className="text-slate-400">Código:</td><td className="text-right font-semibold text-slate-700">{el.codigo}</td></tr>
            <tr><td className="text-slate-400">Ubicación:</td><td className="text-right font-semibold text-slate-700">{el.ubicacion}</td></tr>
            <tr><td className="text-slate-400">Dimensiones:</td><td className="text-right font-semibold text-slate-700">{n2(el.largo)} × {n2(el.ancho)} × {n2(el.alto)} m</td></tr>
            <tr><td className="text-slate-400">Cantidad:</td><td className="text-right font-semibold text-slate-700">{n2(el.cantidad)} und</td></tr>
            {isGrid && <tr><td className="text-slate-400">Acero X-X:</td><td className="text-right font-semibold text-slate-700">{el.aceroXX.fierro} @ {n2(distNum(el.aceroXX.dist))} ({el.aceroXX.nelem})</td></tr>}
            {isGrid && <tr><td className="text-slate-400">Acero Y-Y:</td><td className="text-right font-semibold text-slate-700">{el.aceroYY.fierro} @ {n2(distNum(el.aceroYY.dist))} ({el.aceroYY.nelem})</td></tr>}
            {isLong && <tr><td className="text-slate-400">Long. esquina:</td><td className="text-right font-semibold text-slate-700">{el.esquina.fierro} ({el.esquina.nelem})</td></tr>}
            {isLong && <tr><td className="text-slate-400">Long. intermedio:</td><td className="text-right font-semibold text-slate-700">{el.intermedio.fierro} ({el.intermedio.nelem})</td></tr>}
            {isLong && <tr><td className="text-slate-400">Estribo:</td><td className="text-right font-semibold text-slate-700">{el.estribo.fierro} ({el.estribo.nelem})</td></tr>}
            {isTecho && <tr><td className="text-slate-400">Acero +/−:</td><td className="text-right font-semibold text-slate-700">{el.positivo.fierro} / {el.negativo.fierro} · inc {n2((el.incidencia||0)*100)}%</td></tr>}
            <tr><td className="text-slate-400">Recubrimiento:</td><td className="text-right font-semibold text-slate-700">{n2(params.recubrimiento * 100)} cm</td></tr>
            <tr><td className="text-slate-400">Concreto f'c:</td><td className="text-right font-semibold text-slate-700">{n2(params.fc)} kg/cm²</td></tr>
            <tr><td className="text-slate-400">Acero fy:</td><td className="text-right font-semibold text-slate-700">{n2(params.fy)} kg/cm²</td></tr>
            <tr><td className="text-slate-400">Peso aproximado:</td><td className="text-right font-semibold text-slate-700">{n2((el.acero || 0) / 1000)} t</td></tr>
            <tr className="border-t border-slate-100"><td className="text-slate-400 pt-1">Concreto:</td><td className="text-right font-extrabold pt-1" style={{ color: TEAL }} data-testid="vm-est-detail-concreto">{n2(el.concreto)} m³</td></tr>
            <tr><td className="text-slate-400">Acero:</td><td className="text-right font-extrabold" style={{ color: "#b45309" }} data-testid="vm-est-detail-acero">{n2(el.acero)} kg</td></tr>
          </tbody>
        </table>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          <button onClick={onExport} data-testid="vm-est-export-detalle" className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"><FileDown size={12} /> Exportar</button>
          <button onClick={() => window.print()} data-testid="vm-est-imprimir" className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"><Printer size={12} /> Imprimir</button>
        </div>
      </div>
    </aside>
  );
};


/* ── Tabla longitudinal (Columnas / Vigas): esquina + intermedio + estribo ── */
/* ── Tabla longitudinal (Columnas / Vigas): esquina + intermedio + estribo ── EDITABLE ── */
const RebarLongTable = ({ familyKey, rows, resumen, label, luz, selId, onSel, onEdit, params = {} }) => (
  <TableShell label={label} luz={luz} familyKey={familyKey} count={resumen?.tipos} testid={`vm-est-${familyKey}-table`}>
    <thead>
      <tr className="text-[8.5px] font-bold uppercase tracking-wide" style={{ color: "#475569" }}>
        <th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Código</th>
        <th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Ubicación</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={4} style={{ background: HDR_DIM }}>Dimensiones (m)</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={2} style={{ background: HDR_XX }}>Barras long. en esquina</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={2} style={{ background: HDR_YY }}>Barras long. intermedio</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={2} style={{ background: "#faf5ff" }}>Estribo</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: HDR_MAT }}>f'c<br/><span className="text-[7px] font-semibold">kg/cm²</span></th>
        <th className="px-1 py-1 text-center" rowSpan={2} style={{ background: HDR_MAT }}>fy<br/><span className="text-[7px] font-semibold">kg/cm²</span></th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: HDR_C }}>Concreto (m³)</th>
        <th className="px-1 py-1 text-center" rowSpan={2} style={{ background: HDR_A }}>Acero (kg)</th>
      </tr>
      <tr className="text-[8px] font-bold uppercase" style={{ color: "#94a3b8" }}>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_DIM }}>Largo</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Ancho</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Alto</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Cant.</th>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_XX }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_XX }}># elem</th>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_YY }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_YY }}># elem</th>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: "#faf5ff" }}>Fierro</th><th className="px-1 py-0.5" style={{ background: "#faf5ff" }}># elem</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <RowSelect key={r.id} active={r.id === selId} onClick={() => onSel(r.id)} testid={`vm-est-row-${r.id}`}>
          <td className="px-2 py-1 text-left text-[11px] font-extrabold text-slate-800">{r.codigo}</td>
          <td className="px-2 py-1 text-left text-[11px] text-slate-500">{r.ubicacion}</td>
          <td className={CELL + " border-l border-slate-100"}><EditNum v={r.largo} fk={familyKey} id={r.id} path="largo" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.ancho} fk={familyKey} id={r.id} path="ancho" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.alto} fk={familyKey} id={r.id} path="alto" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.cantidad} fk={familyKey} id={r.id} path="cantidad" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.esquina?.fierro} fk={familyKey} id={r.id} path="esquina.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.esquina?.nelem} fk={familyKey} id={r.id} path="esquina.nelem" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.intermedio?.fierro} fk={familyKey} id={r.id} path="intermedio.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.intermedio?.nelem} fk={familyKey} id={r.id} path="intermedio.nelem" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.estribo?.fierro} fk={familyKey} id={r.id} path="estribo.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.estribo?.nelem} fk={familyKey} id={r.id} path="estribo.nelem" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100 font-semibold text-slate-500"}>{n2(params.fc)}</td>
          <td className={CELL + " font-semibold text-slate-500"}>{n2(params.fy)}</td>
          <td className={CELL + " border-l border-slate-100 font-extrabold text-slate-800"} data-testid={`vm-est-${familyKey}-conc-${r.id}`}>{n2(r.concreto)}</td>
          <td className={CELL + " font-extrabold"} style={{ color: "#b45309" }} data-testid={`vm-est-${familyKey}-acero-${r.id}`}>{n2(r.acero)}</td>
        </RowSelect>
      ))}
    </tbody>
    {/* eslint-disable-next-line */}
    <FamResumenRow resumen={resumen} familyKey={familyKey} />
  </TableShell>
);

/* ── Tabla de malla (Zapatas / Placas / Sobrecimiento / Escalera / Cisterna): acero X-X y Y-Y ── EDITABLE ── */
const GridStructTable = ({ familyKey, rows, resumen, label, selId, onSel, onEdit, params = {} }) => {
  const hasEspesor = rows.some((r) => r.espesor !== undefined);
  const zap = familyKey === "zapatas";
  return (
    <TableShell label={label} familyKey={familyKey} count={resumen?.tipos} testid={`vm-est-${familyKey}-table`}>
      <thead>
        <tr className="text-[8.5px] font-bold uppercase tracking-wide" style={{ color: "#475569" }}>
          <th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Código</th>
          <th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Ubicación</th>
          <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={hasEspesor ? 5 : 4} style={{ background: HDR_DIM }}>Dimensiones (m)</th>
          <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={3} style={{ background: HDR_XX }}>Acero en eje X-X</th>
          <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={3} style={{ background: HDR_YY }}>Acero en eje Y-Y</th>
          <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: HDR_MAT }}>f'c<br/><span className="text-[7px] font-semibold">kg/cm²</span></th>
          <th className="px-1 py-1 text-center" rowSpan={2} style={{ background: HDR_MAT }}>fy<br/><span className="text-[7px] font-semibold">kg/cm²</span></th>
          <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: HDR_C }}>Concreto (m³)</th>
          <th className="px-1 py-1 text-center" rowSpan={2} style={{ background: HDR_A }}>Acero (kg)</th>
        </tr>
        <tr className="text-[8px] font-bold uppercase" style={{ color: "#94a3b8" }}>
          <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_DIM }}>Largo</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Ancho</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Alto</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Cant.</th>{hasEspesor ? <th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Esp.</th> : null}
          <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_XX }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_XX }}># elem</th><th className="px-1 py-0.5" style={{ background: HDR_XX }}>Distrib.</th>
          <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_YY }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_YY }}># elem</th><th className="px-1 py-0.5" style={{ background: HDR_YY }}>Distrib.</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <RowSelect key={r.id} active={r.id === selId} onClick={() => onSel(r.id)} testid={`vm-est-row-${r.id}`}>
            <td className="px-2 py-1 text-left text-[11px] font-extrabold text-slate-800">{r.codigo}</td>
            <td className="px-2 py-1 text-left text-[11px] text-slate-500">{r.ubicacion}</td>
            <td className={CELL + " border-l border-slate-100"}><EditNum v={r.largo} fk={familyKey} id={r.id} path="largo" onEdit={onEdit} /></td>
            <td className={CELL}><EditNum v={r.ancho} fk={familyKey} id={r.id} path="ancho" onEdit={onEdit} /></td>
            <td className={CELL}><EditNum v={r.alto} fk={familyKey} id={r.id} path="alto" onEdit={onEdit} /></td>
            <td className={CELL}><EditNum v={r.cantidad} fk={familyKey} id={r.id} path="cantidad" onEdit={onEdit} w="w-11" step="1" /></td>
            {hasEspesor ? <td className={CELL}>{r.espesor !== undefined ? <EditNum v={r.espesor} fk={familyKey} id={r.id} path="espesor" onEdit={onEdit} /> : "—"}</td> : null}
            <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.aceroXX?.fierro} fk={familyKey} id={r.id} path="aceroXX.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.aceroXX?.nelem} fk={familyKey} id={r.id} path="aceroXX.nelem" onEdit={onEdit} w="w-11" step="1" /></td><td className={CELL}><EditDist v={r.aceroXX?.dist} fk={familyKey} id={r.id} path="aceroXX.dist" onEdit={onEdit} /></td>
            <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.aceroYY?.fierro} fk={familyKey} id={r.id} path="aceroYY.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.aceroYY?.nelem} fk={familyKey} id={r.id} path="aceroYY.nelem" onEdit={onEdit} w="w-11" step="1" /></td><td className={CELL}><EditDist v={r.aceroYY?.dist} fk={familyKey} id={r.id} path="aceroYY.dist" onEdit={onEdit} /></td>
            <td className={CELL + " border-l border-slate-100 font-semibold text-slate-500"}>{n2(params.fc)}</td>
            <td className={CELL + " font-semibold text-slate-500"}>{n2(params.fy)}</td>
            <td className={CELL + " border-l border-slate-100 font-extrabold text-slate-800"} data-testid={zap ? `vm-est-zap-conc-${r.id}` : `vm-est-${familyKey}-conc-${r.id}`}>{n2(r.concreto)}</td>
            <td className={CELL + " font-extrabold"} style={{ color: "#b45309" }} data-testid={zap ? `vm-est-zap-acero-${r.id}` : `vm-est-${familyKey}-acero-${r.id}`}>{n2(r.acero)}</td>
          </RowSelect>
        ))}
      </tbody>
      <FamResumenRow resumen={resumen} familyKey={familyKey} />
    </TableShell>
  );
};

/* ── Tabla de Techo / Losa Aligerada ── EDITABLE ── */
const TechoTable = ({ familyKey, rows, resumen, label, selId, onSel, onEdit }) => (
  <TableShell label={label} familyKey={familyKey} count={resumen?.tipos} testid={`vm-est-${familyKey}-table`}>
    <thead>
      <tr className="text-[8.5px] font-bold uppercase tracking-wide" style={{ color: "#475569" }}>
        <th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Código</th><th className="px-2 py-1 text-left" rowSpan={2} style={{ background: "#f1f5f9" }}>Ubicación</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={4} style={{ background: HDR_DIM }}>Dimensiones (m)</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={2} style={{ background: HDR_XX }}>Acero positivo</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" colSpan={2} style={{ background: HDR_YY }}>Acero negativo</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: "#faf5ff" }}>% Inc.</th>
        <th className="px-1 py-0.5 text-center border-l border-slate-200" rowSpan={2} style={{ background: HDR_C }}>Concreto (m³)</th>
        <th className="px-1 py-1 text-center" rowSpan={2} style={{ background: HDR_A }}>Acero (kg)</th>
      </tr>
      <tr className="text-[8px] font-bold uppercase" style={{ color: "#94a3b8" }}>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_DIM }}>Largo</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Ancho</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Alto</th><th className="px-1 py-0.5" style={{ background: HDR_DIM }}>Cant.</th>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_XX }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_XX }}># elem</th>
        <th className="px-1 py-0.5 border-l border-slate-200" style={{ background: HDR_YY }}>Fierro</th><th className="px-1 py-0.5" style={{ background: HDR_YY }}># elem</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <RowSelect key={r.id} active={r.id === selId} onClick={() => onSel(r.id)} testid={`vm-est-row-${r.id}`}>
          <td className="px-2 py-1 text-left text-[11px] font-extrabold text-slate-800">{r.codigo}</td>
          <td className="px-2 py-1 text-left text-[11px] text-slate-500">{r.ubicacion}</td>
          <td className={CELL + " border-l border-slate-100"}><EditNum v={r.largo} fk={familyKey} id={r.id} path="largo" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.ancho} fk={familyKey} id={r.id} path="ancho" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.alto} fk={familyKey} id={r.id} path="alto" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.cantidad} fk={familyKey} id={r.id} path="cantidad" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.positivo?.fierro} fk={familyKey} id={r.id} path="positivo.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.positivo?.nelem} fk={familyKey} id={r.id} path="positivo.nelem" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100"}><EditFierro v={r.negativo?.fierro} fk={familyKey} id={r.id} path="negativo.fierro" onEdit={onEdit} /></td><td className={CELL}><EditNum v={r.negativo?.nelem} fk={familyKey} id={r.id} path="negativo.nelem" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100 text-slate-700"}>{n2((r.incidencia || 0) * 100)}%</td>
          <td className={CELL + " border-l border-slate-100 font-extrabold text-slate-800"} data-testid={`vm-est-${familyKey}-conc-${r.id}`}>{n2(r.concreto)}</td>
          <td className={CELL + " font-extrabold"} style={{ color: "#b45309" }} data-testid={`vm-est-${familyKey}-acero-${r.id}`}>{n2(r.acero)}</td>
        </RowSelect>
      ))}
    </tbody>
    <FamResumenRow resumen={resumen} familyKey={familyKey} />
  </TableShell>
);

/* ── Motor COREX · Losa Aligerada (cálculo determinista en backend · ADR-COREX-001) ── */
const ESPESORES = [{ v: 0.17, label: "17 cm" }, { v: 0.20, label: "20 cm" }, { v: 0.25, label: "25 cm" }];
const LosaAligeradaMotor = ({ ctx, el, onEdit, params }) => {
  const [espesor, setEspesor] = React.useState(Number(el?.espesor) || 0.20);
  const [busy, setBusy] = React.useState(false);
  const [res, setRes] = React.useState(null);
  const [synced, setSynced] = React.useState(false);
  const [error, setError] = React.useState("");
  const largo = Number(ctx?.largo) || 0;
  const ancho = Number(ctx?.ancho) || 0;

  const sincronizar = async (esp) => {
    const e = esp ?? espesor;
    setBusy(true); setError(""); setSynced(false);
    try {
      const inputs = {
        largo, ancho, espesor: e, cantidad: 1,
        acero_positivo: { diametro: el?.positivo?.fierro || "1/2\"", n: Number(el?.positivo?.nelem) || 1 },
        acero_negativo: { diametro: el?.negativo?.fierro || "1/2\"", n: Number(el?.negativo?.nelem) || 1 },
        acero_temperatura: { diametro: el?.temperatura?.fierro || "1/4\"", espaciamiento: 0.25 },
      };
      const { data } = await api.post("/corex-engine/calcular", { elemento: "losa_aligerada", nivel: "1er_piso", inputs });
      if (data?.resultado && !data.error) { setRes(data.resultado); setSynced(true); }
      else setError(data?.error || "El motor no devolvió resultados.");
    } catch (ex) { setError(humanizeAxiosError(ex)); }
    finally { setBusy(false); }
  };

  React.useEffect(() => { if (largo && ancho) sincronizar(espesor); /* eslint-disable-next-line */ }, [largo, ancho]);

  const pickEspesor = (v) => { setEspesor(v); setSynced(false); if (el?.id && onEdit) onEdit("techo", el.id, "espesor", v); sincronizar(v); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="vm-est-losa-motor">
      <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ background: "linear-gradient(135deg,#0c2b38,#0ea5b7)" }}>
        <Cpu size={15} />
        <div className="text-[12px] font-extrabold uppercase tracking-wide">Motor COREX · Losa Aligerada</div>
        {synced
          ? <span className="ml-auto flex items-center gap-1 rounded-md bg-emerald-500/20 border border-emerald-300/40 px-2 py-0.5 text-[10px] font-bold text-emerald-200" data-testid="vm-est-losa-sync-badge"><Check size={11} /> Sincronizado con motor COREX</span>
          : <span className="ml-auto text-[10px] font-semibold text-white/60" data-testid="vm-est-losa-sync-badge">Pendiente de sincronizar</span>}
      </div>
      <div className="p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase text-slate-500">Espesor de losa:</span>
          {ESPESORES.map((o) => (
            <button key={o.v} data-testid={`vm-est-losa-espesor-${Math.round(o.v * 100)}`} onClick={() => pickEspesor(o.v)}
              className="rounded-lg px-3 py-1.5 text-[12px] font-extrabold transition-colors"
              style={espesor === o.v ? { background: TEAL, color: "#fff" } : { background: "#eef2f5", color: "#64748b" }}>
              {o.label}
            </button>
          ))}
          <button onClick={() => sincronizar()} disabled={busy || !largo || !ancho} data-testid="vm-est-losa-sync-btn"
            className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-extrabold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#0ea5b7,#16a34a)" }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sigma size={14} />} Sincronizar con motor
          </button>
          <button onClick={() => res && generarMemoriaCalculo({ el: { ...el, espesor }, familyKey: "techo", params: params || {}, ctx, losa: res })}
            disabled={!res} data-testid="vm-est-losa-pdf-btn"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-extrabold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
            <FileDown size={14} /> Memoria PDF
          </button>
        </div>
        <div className="text-[10px] text-slate-400">Área de losa heredada de Inicio: {n2(largo)} m × {n2(ancho)} m. El motor calcula concreto neto, ladrillos y acero (ADR-COREX-001 · el frontend NO calcula).</div>
        {error ? <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[11px] font-semibold text-red-700" data-testid="vm-est-losa-error">{error}</div> : null}
        {res ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="vm-est-losa-result">
            <ResumenCard label="Concreto neto" value={n2(res.concreto_m3)} unit={`m³ · ${res.coef_concreto_m3_m2} m³/m²`} accent={TEAL} testid="vm-est-losa-concreto" />
            <ResumenCard label="Ladrillos" value={res.ladrillos_und} unit="und" testid="vm-est-losa-ladrillos" />
            <ResumenCard label="Acero viguetas" value={n2(res.acero_viguetas_kg)} unit="kg" accent="#b45309" testid="vm-est-losa-acero-viguetas" />
            <ResumenCard label="Acero temperatura" value={n2(res.acero_temperatura_kg)} unit="kg" accent="#b45309" testid="vm-est-losa-acero-temp" />
            <ResumenCard label="Acero total (+5%)" value={n2(res.acero_kg)} unit="kg" accent="#dc2626" testid="vm-est-losa-acero-total" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ── Motor COREX · Placas (sección rectangular larga · doble cortina) ── */
const PlacasMotor = ({ ctx, rows, params }) => {
  const placaRows = (rows || []).filter((r) => String(r.ubicacion || "").toLowerCase() === "placa");
  const [res, setRes] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [synced, setSynced] = React.useState(false);
  const key = JSON.stringify(placaRows.map((p) => [p.largo, p.ancho, p.alto, p.cantidad, p.aceroXX, p.aceroYY]));

  const sincronizar = async () => {
    setBusy(true); setSynced(false);
    try {
      const out = {};
      for (const p of placaRows) {
        const inputs = {
          largo_m: Number(p.largo) || 0, ancho_m: Number(p.ancho) || 0,
          alto_m: Number(p.alto) || Number(ctx?.altura1) || 3.0, cantidad: Number(p.cantidad) || 0,
          acero_vertical: { diametro: p.aceroYY?.fierro || "1/2\"", dist: distNum(p.aceroYY?.dist) || 0.25 },
          acero_horizontal: { diametro: p.aceroXX?.fierro || "3/8\"", dist: distNum(p.aceroXX?.dist) || 0.25 },
        };
        const { data } = await api.post("/corex-engine/calcular", { elemento: "placas", nivel: "1er_piso", inputs });
        if (data?.resultado && !data.error) out[p.id] = data.resultado;
      }
      setRes(out); setSynced(true);
    } catch (e) { /* noop */ }
    finally { setBusy(false); }
  };

  React.useEffect(() => { if (placaRows.length) sincronizar(); /* eslint-disable-next-line */ }, [key]);

  const agg = Object.values(res).reduce((a, r) => ({
    conc: a.conc + (r.concreto_m3 || 0), enc: a.enc + (r.encofrado_m2 || 0), ac: a.ac + (r.acero_kg || 0),
  }), { conc: 0, enc: 0, ac: 0 });
  const totalCant = placaRows.reduce((s, p) => s + (Number(p.cantidad) || 0), 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="vm-est-placas-motor">
      <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ background: "linear-gradient(135deg,#0c2b38,#2563eb)" }}>
        <Cpu size={15} />
        <div className="text-[12px] font-extrabold uppercase tracking-wide">Motor COREX · Placas</div>
        {synced
          ? <span className="ml-auto flex items-center gap-1 rounded-md bg-emerald-500/20 border border-emerald-300/40 px-2 py-0.5 text-[10px] font-bold text-emerald-200" data-testid="vm-est-placas-sync-badge"><Check size={11} /> Sincronizado con motor COREX</span>
          : <span className="ml-auto text-[10px] font-semibold text-white/60" data-testid="vm-est-placas-sync-badge">Pendiente de sincronizar</span>}
      </div>
      <div className="p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold text-slate-500">Sección rectangular larga (largo &gt; ancho) · doble cortina vertical/horizontal. {totalCant === 0 ? "Proyecto sin placas (cantidad 0) — edita la cantidad para activarlas." : `${n2(totalCant)} placa(s) activa(s).`}</span>
          <button onClick={sincronizar} disabled={busy} data-testid="vm-est-placas-sync-btn"
            className="ml-auto flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-extrabold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#2563eb,#0ea5b7)" }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sigma size={14} />} Sincronizar con motor
          </button>
          <button onClick={() => { const p = placaRows[0]; const r = p && res[p.id]; if (p && r) generarMemoriaCalculo({ el: { ...p, concreto: r.concreto_m3, acero: r.acero_kg }, familyKey: "placas", params: params || {}, ctx }); }}
            disabled={!placaRows[0] || !res[placaRows[0]?.id]} data-testid="vm-est-placas-pdf-btn"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-extrabold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
            <FileDown size={14} /> Memoria PDF
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="vm-est-placas-result">
          <ResumenCard label="Concreto total" value={n2(agg.conc)} unit="m³" accent={TEAL} testid="vm-est-placas-concreto" />
          <ResumenCard label="Encofrado total" value={n2(agg.enc)} unit="m²" testid="vm-est-placas-encofrado" />
          <ResumenCard label="Acero total (+5%)" value={n2(agg.ac)} unit="kg" accent="#b45309" testid="vm-est-placas-acero" />
        </div>
        {placaRows.length ? (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-[10.5px]">
              <thead><tr className="text-[9px] font-bold uppercase text-slate-500" style={{ background: "#f1f5f9" }}>
                <th className="px-2 py-1 text-left">Placa</th><th className="px-2 py-1 text-right">Concreto m³</th><th className="px-2 py-1 text-right">Encofrado m²</th><th className="px-2 py-1 text-right">Acero vert. kg</th><th className="px-2 py-1 text-right">Acero horiz. kg</th><th className="px-2 py-1 text-right">Acero kg</th>
              </tr></thead>
              <tbody>
                {placaRows.map((p) => { const r = res[p.id] || {}; return (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-semibold text-slate-700">{p.codigo} · {n2(p.largo)}×{n2(p.ancho)}×{n2(p.alto)} m ({n2(p.cantidad)})</td>
                    <td className="px-2 py-1 text-right">{n2(r.concreto_m3)}</td>
                    <td className="px-2 py-1 text-right">{n2(r.encofrado_m2)}</td>
                    <td className="px-2 py-1 text-right">{n2(r.acero_vertical_kg)}</td>
                    <td className="px-2 py-1 text-right">{n2(r.acero_horizontal_kg)}</td>
                    <td className="px-2 py-1 text-right font-extrabold" style={{ color: "#b45309" }}>{n2(r.acero_kg)}</td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="text-[10px] text-slate-400">Edita dimensiones y acero en la tabla de Placas · el motor recalcula (ADR-COREX-001) y el presupuesto reacciona vía el Puente Maestro.</div>
      </div>
    </div>
  );
};

/* ── Tabla plana (Cimiento Corrido): dimensiones + concreto, sin acero ── EDITABLE ── */
const PlainTable = ({ familyKey, rows, resumen, label, selId, onSel, onEdit }) => (
  <TableShell label={label} familyKey={familyKey} count={resumen?.tipos} testid={`vm-est-${familyKey}-table`}>
    <thead>
      <tr className="text-[8.5px] font-bold uppercase tracking-wide" style={{ background: "#f1f5f9", color: "#64748b" }}>
        <th className="px-2 py-1 text-left">Código</th><th className="px-2 py-1 text-left">Ubicación</th>
        <th className="px-1 py-1.5 border-l border-slate-200">Largo (m)</th><th className="px-1 py-1.5">Ancho (m)</th><th className="px-1 py-1.5">Alto (m)</th><th className="px-1 py-1.5">Cant.</th>
        <th className="px-1 py-1.5 border-l border-slate-200">Concreto (m³)</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <RowSelect key={r.id} active={r.id === selId} onClick={() => onSel(r.id)} testid={`vm-est-row-${r.id}`}>
          <td className="px-2 py-1 text-left text-[11px] font-extrabold text-slate-800">{r.codigo}</td>
          <td className="px-2 py-1 text-left text-[11px] text-slate-500">{r.ubicacion}</td>
          <td className={CELL + " border-l border-slate-100"}><EditNum v={r.largo} fk={familyKey} id={r.id} path="largo" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.ancho} fk={familyKey} id={r.id} path="ancho" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.alto} fk={familyKey} id={r.id} path="alto" onEdit={onEdit} /></td>
          <td className={CELL}><EditNum v={r.cantidad} fk={familyKey} id={r.id} path="cantidad" onEdit={onEdit} w="w-11" step="1" /></td>
          <td className={CELL + " border-l border-slate-100 font-extrabold text-slate-800"} data-testid={`vm-est-${familyKey}-conc-${r.id}`}>{n2(r.concreto)}</td>
        </RowSelect>
      ))}
    </tbody>
    <FamResumenRow resumen={resumen} familyKey={familyKey} />
  </TableShell>
);

/* Resumen embebido como pie de tabla (tfoot) */
const FamResumenRow = ({ resumen, familyKey }) => (
  <tfoot>
    <tr>
      <td colSpan={20} className="p-0">
        <FamResumen resumen={resumen} familyKey={familyKey} />
      </td>
    </tr>
  </tfoot>
);

